const responseTimeoutMs = 5 * 60 * 1000;

type PendingResponse = {
    resolve: () => void;
    timeoutId: number;
};

type ChatMessageDispatcherOptions = {
    onTimeout?: () => void;
    onPendingQueueEmpty?: () => void;
};

export class ChatMessageDispatcher {
    private pendingResponses = new Map<string, PendingResponse>();
    private earlyResolvedClientMessageIds = new Set<string>();
    private readonly onTimeout?: () => void;
    private readonly onPendingQueueEmpty?: () => void;

    public constructor(options: ChatMessageDispatcherOptions) {
        this.onTimeout = options.onTimeout;
        this.onPendingQueueEmpty = options.onPendingQueueEmpty;
    }

    public clearAllPendingResponses(): void {
        this.pendingResponses.forEach(pendingResponse => {
            window.clearTimeout(pendingResponse.timeoutId);
            pendingResponse.resolve();
        });

        this.pendingResponses.clear();
        this.earlyResolvedClientMessageIds.clear();
    }

    public resolvePendingResponse(clientMessageId: string | null | undefined, hasError: boolean): void {
        if (!clientMessageId) {
            return;
        }

        const pendingResponse = this.pendingResponses.get(clientMessageId);
        if (!pendingResponse) {
            this.earlyResolvedClientMessageIds.add(clientMessageId);
            if (hasError) {
                this.onTimeout?.();
            }
            return;
        }

        window.clearTimeout(pendingResponse.timeoutId);
        pendingResponse.resolve();
        this.pendingResponses.delete(clientMessageId);

        if (hasError) {
            this.onTimeout?.();
        }

        if (this.pendingResponses.size === 0) {
            this.onPendingQueueEmpty?.();
        }
    }

    public resolveFromMessage(clientMessageId?: string | null): void {
        if (clientMessageId == null) {
            return;
        }

        this.resolvePendingResponse(clientMessageId, false);
    }

    public waitForResponseMessages(clientMessageIds: string[]): Promise<void> {
        return new Promise(resolve => {
            const uniqueClientMessageIds = Array.from(new Set(clientMessageIds));
            if (uniqueClientMessageIds.length === 0) {
                this.onPendingQueueEmpty?.();
                resolve();
                return;
            }

            let pendingCount = 0;
            uniqueClientMessageIds.forEach(clientMessageId => {
                if (this.earlyResolvedClientMessageIds.has(clientMessageId)) {
                    this.earlyResolvedClientMessageIds.delete(clientMessageId);
                    return;
                }

                pendingCount += 1;
                const timeoutId = window.setTimeout(() => {
                    this.resolvePendingResponse(clientMessageId, true);
                }, responseTimeoutMs);

                this.pendingResponses.set(clientMessageId, {
                    timeoutId,
                    resolve: () => {
                        pendingCount -= 1;
                        if (pendingCount === 0) {
                            resolve();
                        }
                    }
                });
            });

            if (pendingCount === 0) {
                resolve();
                if (this.pendingResponses.size === 0) {
                    this.onPendingQueueEmpty?.();
                }
            }
        });
    }
}
